import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Upload,
  Star,
  ChevronUp,
  ChevronDown,
  Save,
  X,
  Image as ImageIcon,
} from "lucide-react";

const TIER_KEYS = ["essential", "signature", "prestige"];
const TIER_LABELS = { essential: "Essentiel", signature: "Signature", prestige: "Prestige" };

/* ---------------- Image Uploader (shared pattern) ---------------- */
function ImageUploader({ multiple = true, onUploaded, testid = "uploader" }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (onUploaded) await onUploaded(r.data.url);
      }
      toast.success(`${files.length} photo(s) importée(s)`);
    } catch {
      toast.error("Import échoué");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" multiple={multiple} onChange={handle} className="hidden" data-testid={`${testid}-input`} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
        className="border border-[#0A1128]/30 text-[#0A1128] px-4 py-2 text-xs tracking-[0.2em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center gap-2 disabled:opacity-60"
        data-testid={`${testid}-button`}>
        <Upload size={14} /> {uploading ? "..." : (multiple ? "Importer" : "Importer")}
      </button>
    </>
  );
}

/* ---------------- Inclusions list editor ---------------- */
function InclusionsEditor({ list, onChange, testid = "incl" }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...list, v]);
    setDraft("");
  };
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  return (
    <div>
      <ul className="space-y-1">
        {list.map((it, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-[#0A1128] py-1" data-testid={`${testid}-item-${i}`}>
            <span className="text-[#D4AF37]">◆</span>
            <span className="flex-1">{it}</span>
            <button onClick={() => remove(i)} className="text-[#E2856E] hover:text-[#0A1128]"><X size={12} /></button>
          </li>
        ))}
        {list.length === 0 && <li className="text-xs italic text-[#0A1128]/40">Aucune inclusion.</li>}
      </ul>
      <div className="flex gap-2 mt-3">
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Ajouter une inclusion" className="flex-1 border border-[#0A1128]/15 px-3 py-2 text-sm"
          data-testid={`${testid}-input`} />
        <button type="button" onClick={add} className="border border-[#0A1128]/30 px-3 py-2 text-xs uppercase tracking-wide hover:border-[#D4AF37]" data-testid={`${testid}-add`}>+</button>
      </div>
    </div>
  );
}

/* ---------------- Tier editor ---------------- */
function TierEditor({ tierKey, tier, onChange }) {
  const set = (k, v) => onChange({ ...tier, [k]: v });
  return (
    <div className="border border-[#D4AF37]/30 bg-[#F9F8F6] p-5" data-testid={`tier-${tierKey}`}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="overline text-[#D4AF37]">{TIER_LABELS[tierKey]}</div>
          <div className="text-xs text-[#0A1128]/50">Formule {TIER_LABELS[tierKey].toLowerCase()}</div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <input placeholder="Titre (FR)" value={tier.title_fr || ""} onChange={(e) => set("title_fr", e.target.value)} className="border border-[#0A1128]/15 px-3 py-2 text-sm" data-testid={`tier-${tierKey}-title-fr`} />
        <input placeholder="Titre (EN)" value={tier.title_en || ""} onChange={(e) => set("title_en", e.target.value)} className="border border-[#0A1128]/15 px-3 py-2 text-sm" data-testid={`tier-${tierKey}-title-en`} />
      </div>
      <div className="grid md:grid-cols-2 gap-3 mt-3">
        <textarea rows={3} placeholder="Description (FR)" value={tier.description_fr || ""} onChange={(e) => set("description_fr", e.target.value)} className="border border-[#0A1128]/15 px-3 py-2 text-sm resize-none" data-testid={`tier-${tierKey}-desc-fr`} />
        <textarea rows={3} placeholder="Description (EN)" value={tier.description_en || ""} onChange={(e) => set("description_en", e.target.value)} className="border border-[#0A1128]/15 px-3 py-2 text-sm resize-none" />
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div>
          <div className="overline text-[#0A1128]/60 mb-2">Inclusions (FR)</div>
          <InclusionsEditor list={tier.inclusions_fr || []} onChange={(v) => set("inclusions_fr", v)} testid={`tier-${tierKey}-incl-fr`} />
        </div>
        <div>
          <div className="overline text-[#0A1128]/60 mb-2">Inclusions (EN)</div>
          <InclusionsEditor list={tier.inclusions_en || []} onChange={(v) => set("inclusions_en", v)} testid={`tier-${tierKey}-incl-en`} />
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-3 mt-4">
        <div>
          <label className="overline text-[#0A1128]/60">Prix à partir de</label>
          <input type="number" min={0} value={tier.price_from || 0} onChange={(e) => set("price_from", Number(e.target.value))} className="w-full border-b border-[#0A1128]/20 py-2 focus:outline-none focus:border-[#D4AF37]" data-testid={`tier-${tierKey}-price`} />
        </div>
        <div>
          <label className="overline text-[#0A1128]/60">Devise</label>
          <select value={tier.currency || "EUR"} onChange={(e) => set("currency", e.target.value)} className="w-full border-b border-[#0A1128]/20 py-2 focus:outline-none focus:border-[#D4AF37] bg-transparent">
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="GBP">GBP</option>
          </select>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Stay editor panel ---------------- */
function StayPanel({ stay, index, total, onChange, onMoveUp, onMoveDown, onDelete }) {
  const [meta, setMeta] = useState({
    slug: stay.slug || "",
    name_fr: stay.name_fr || "",
    name_en: stay.name_en || "",
    region: stay.region || "",
    description_fr: stay.description_fr || "",
    description_en: stay.description_en || "",
  });
  const [tiers, setTiers] = useState({
    essential: stay.essential || {},
    signature: stay.signature || {},
    prestige: stay.prestige || {},
  });
  const [saving, setSaving] = useState(false);

  const dirty =
    meta.slug !== (stay.slug || "") ||
    meta.name_fr !== (stay.name_fr || "") ||
    meta.name_en !== (stay.name_en || "") ||
    meta.region !== (stay.region || "") ||
    meta.description_fr !== (stay.description_fr || "") ||
    meta.description_en !== (stay.description_en || "") ||
    JSON.stringify(tiers.essential) !== JSON.stringify(stay.essential || {}) ||
    JSON.stringify(tiers.signature) !== JSON.stringify(stay.signature || {}) ||
    JSON.stringify(tiers.prestige) !== JSON.stringify(stay.prestige || {});

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/stays/${stay.stay_id}`, { ...meta, ...tiers });
      toast.success("Séjour enregistré");
      onChange();
    } catch {
      toast.error("Échec");
    } finally {
      setSaving(false);
    }
  };

  const addImage = async (url) => {
    await api.post(`/admin/stays/${stay.stay_id}/images`, { url, is_primary: false });
    onChange();
  };
  const setPrimary = async (img_id) => {
    await api.patch(`/admin/stays/${stay.stay_id}/images/${img_id}`, { is_primary: true });
    toast.success("Photo principale mise à jour");
    onChange();
  };
  const removeImage = async (img_id) => {
    await api.delete(`/admin/stays/${stay.stay_id}/images/${img_id}`);
    onChange();
  };

  const images = [...(stay.images || [])].sort((a, b) => a.order - b.order);

  return (
    <div className="bg-white border border-[#0A1128]/10" data-testid={`stay-${stay.stay_id}`}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#0A1128]/10 bg-[#0A1128]/[0.02]">
        <div className="flex items-center gap-4">
          <div className="flex flex-col">
            <button onClick={onMoveUp} disabled={index === 0} className="text-[#0A1128]/60 hover:text-[#D4AF37] disabled:opacity-30" data-testid={`stay-up-${stay.stay_id}`}><ChevronUp size={16} /></button>
            <button onClick={onMoveDown} disabled={index === total - 1} className="text-[#0A1128]/60 hover:text-[#D4AF37] disabled:opacity-30" data-testid={`stay-down-${stay.stay_id}`}><ChevronDown size={16} /></button>
          </div>
          <div>
            <div className="overline text-[#D4AF37]">{meta.region || "Séjour"}</div>
            <div className="font-serif text-2xl text-[#0A1128]">{meta.name_fr || meta.slug || "(sans nom)"}</div>
            <div className="text-xs text-[#0A1128]/40">/{meta.slug}</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {dirty && (
            <button onClick={save} disabled={saving} className="bg-[#0A1128] text-[#F9F8F6] px-5 py-2 text-xs tracking-[0.24em] uppercase flex items-center gap-2 disabled:opacity-60" data-testid={`stay-save-${stay.stay_id}`}>
              <Save size={14} /> {saving ? "..." : "Enregistrer"}
            </button>
          )}
          <button onClick={onDelete} className="text-[#E2856E] hover:text-[#0A1128]" data-testid={`stay-delete-${stay.stay_id}`}>
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {/* Meta */}
      <div className="p-6 space-y-4 border-b border-[#0A1128]/10">
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="overline text-[#0A1128]/60">Slug</label>
            <input value={meta.slug} onChange={(e) => setMeta({ ...meta, slug: e.target.value })} className="w-full border-b border-[#0A1128]/20 py-2 focus:outline-none focus:border-[#D4AF37] mt-1" data-testid={`stay-slug-${stay.stay_id}`} />
          </div>
          <div>
            <label className="overline text-[#0A1128]/60">Région</label>
            <input value={meta.region} onChange={(e) => setMeta({ ...meta, region: e.target.value })} className="w-full border-b border-[#0A1128]/20 py-2 focus:outline-none focus:border-[#D4AF37] mt-1" data-testid={`stay-region-${stay.stay_id}`} />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="overline text-[#0A1128]/60">Nom (FR)</label>
            <input value={meta.name_fr} onChange={(e) => setMeta({ ...meta, name_fr: e.target.value })} className="w-full border-b border-[#0A1128]/20 py-2 focus:outline-none focus:border-[#D4AF37] mt-1" data-testid={`stay-name-fr-${stay.stay_id}`} />
          </div>
          <div>
            <label className="overline text-[#0A1128]/60">Nom (EN)</label>
            <input value={meta.name_en} onChange={(e) => setMeta({ ...meta, name_en: e.target.value })} className="w-full border-b border-[#0A1128]/20 py-2 focus:outline-none focus:border-[#D4AF37] mt-1" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="overline text-[#0A1128]/60">Description générale (FR)</label>
            <textarea rows={3} value={meta.description_fr} onChange={(e) => setMeta({ ...meta, description_fr: e.target.value })} className="w-full border border-[#0A1128]/15 px-3 py-2 text-sm resize-none mt-1" data-testid={`stay-desc-fr-${stay.stay_id}`} />
          </div>
          <div>
            <label className="overline text-[#0A1128]/60">Description générale (EN)</label>
            <textarea rows={3} value={meta.description_en} onChange={(e) => setMeta({ ...meta, description_en: e.target.value })} className="w-full border border-[#0A1128]/15 px-3 py-2 text-sm resize-none mt-1" />
          </div>
        </div>
      </div>

      {/* Photos */}
      <div className="p-6 border-b border-[#0A1128]/10">
        <div className="flex items-center justify-between mb-4">
          <div className="overline text-[#D4AF37]">Photos ({images.length})</div>
          <ImageUploader multiple onUploaded={addImage} testid={`stay-images-${stay.stay_id}`} />
        </div>
        {images.length === 0 && <div className="text-xs italic text-[#0A1128]/40">Aucune photo importée.</div>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {images.map((img) => (
            <div key={img.img_id} className={`relative group border ${img.is_primary ? "border-[#D4AF37]" : "border-[#0A1128]/10"}`} data-testid={`stay-image-${img.img_id}`}>
              <img src={img.url} alt="" className="w-full h-32 object-cover" />
              {img.is_primary && (
                <div className="absolute top-2 left-2 bg-[#D4AF37] text-[#0A1128] px-2 py-0.5 text-[10px] tracking-wide uppercase font-medium flex items-center gap-1"><Star size={10} /> Principale</div>
              )}
              <div className="absolute inset-0 bg-[#0A1128]/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                {!img.is_primary && (
                  <button onClick={() => setPrimary(img.img_id)} className="text-white text-xs tracking-wide uppercase flex items-center gap-1" data-testid={`stay-image-primary-${img.img_id}`}>
                    <Star size={14} /> Principale
                  </button>
                )}
                <button onClick={() => removeImage(img.img_id)} className="text-[#F0C85A] hover:text-white" data-testid={`stay-image-delete-${img.img_id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tiers */}
      <div className="p-6 space-y-6">
        <div className="overline text-[#D4AF37]">Formules tarifaires</div>
        {TIER_KEYS.map((k) => (
          <TierEditor key={k} tierKey={k} tier={tiers[k] || {}} onChange={(v) => setTiers({ ...tiers, [k]: v })} />
        ))}
      </div>
    </div>
  );
}

/* ---------------- Stays Manager (root) ---------------- */
export default function StaysManager({ onSitePublicRefresh }) {
  const [stays, setStays] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newStay, setNewStay] = useState({ slug: "", name_fr: "", region: "" });

  const load = async () => {
    try {
      const r = await api.get("/admin/stays");
      setStays(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const refreshAll = async () => {
    await load();
    if (onSitePublicRefresh) onSitePublicRefresh();
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/stays", newStay);
      toast.success("Séjour créé");
      setNewStay({ slug: "", name_fr: "", region: "" });
      setCreating(false);
      refreshAll();
    } catch {
      toast.error("Échec");
    }
  };

  const remove = async (stay) => {
    if (!window.confirm(`Supprimer « ${stay.name_fr || stay.slug} » ?`)) return;
    await api.delete(`/admin/stays/${stay.stay_id}`);
    toast.success("Séjour supprimé");
    refreshAll();
  };

  const move = async (index, dir) => {
    const arr = [...stays];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    await api.post("/admin/stays/reorder", { order: arr.map((s) => s.stay_id) });
    refreshAll();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-3xl text-[#0A1128]">Séjours & formules</h2>
          <p className="text-sm text-[#0A1128]/60 mt-1">Créez des séjours avec 3 formules tarifaires (Essentiel · Signature · Prestige).</p>
        </div>
        {!creating && (
          <button onClick={() => setCreating(true)} className="bg-[#0A1128] text-[#F9F8F6] px-5 py-2.5 text-xs tracking-[0.24em] uppercase hover:bg-[#D4AF37] hover:text-[#0A1128] transition-colors flex items-center gap-2" data-testid="stay-new">
            <Plus size={14} /> Nouveau séjour
          </button>
        )}
      </div>

      {creating && (
        <form onSubmit={create} className="bg-white border border-[#D4AF37]/40 p-6 grid md:grid-cols-4 gap-3" data-testid="stay-new-form">
          <input required placeholder="Slug (ex: champagne-escape)" value={newStay.slug} onChange={(e) => setNewStay({ ...newStay, slug: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm" data-testid="stay-new-slug" />
          <input required placeholder="Nom (FR)" value={newStay.name_fr} onChange={(e) => setNewStay({ ...newStay, name_fr: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm md:col-span-2" data-testid="stay-new-name" />
          <input placeholder="Région" value={newStay.region} onChange={(e) => setNewStay({ ...newStay, region: e.target.value })} className="border border-[#0A1128]/15 px-3 py-2 text-sm" />
          <div className="md:col-span-4 flex gap-3">
            <button type="submit" className="bg-[#0A1128] text-[#F9F8F6] px-5 py-2 text-xs tracking-[0.24em] uppercase" data-testid="stay-new-submit">Créer</button>
            <button type="button" onClick={() => setCreating(false)} className="text-xs tracking-[0.24em] uppercase text-[#0A1128]/60 hover:text-[#0A1128]">Annuler</button>
          </div>
        </form>
      )}

      {stays.length === 0 && !creating && <div className="text-[#0A1128]/50 italic">Aucun séjour. Créez le premier ci-dessus.</div>}

      {stays.map((s, i) => (
        <StayPanel key={s.stay_id} stay={s} index={i} total={stays.length}
          onChange={refreshAll}
          onMoveUp={() => move(i, -1)} onMoveDown={() => move(i, 1)}
          onDelete={() => remove(s)} />
      ))}
    </div>
  );
}
